import React, { useEffect, useRef, useState } from 'react';

/**
 * Custom filter select (Headless UI / Radix-style).
 * Arrow tracks real open state: open → up, close / same-click → down.
 */
export function AdminSelect({ value, onChange, options = [], className = '', 'aria-label': ariaLabel }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selected = options.find((o) => String(o.value) === String(value)) || options[0];

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={`admin-select${open ? ' is-open' : ''}${className ? ` ${className}` : ''}`} ref={rootRef}>
      {/* Invisible sizer: trigger width = longest option (stable, no wrap) */}
      <span className="admin-select-sizer" aria-hidden="true">
        {options.map((opt) => (
          <span key={`sizer-${String(opt.value)}`} className="admin-select-sizer-item">{opt.label}</span>
        ))}
      </span>
      <button
        type="button"
        className="admin-select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="admin-select-label">{selected ? selected.label : ''}</span>
        <i className={`fas fa-chevron-down admin-select-chevron${open ? ' is-open' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <ul className="admin-select-menu" role="listbox">
          {options.map((opt) => {
            const active = String(opt.value) === String(value);
            return (
              <li key={String(opt.value) || '__empty'}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={`admin-select-option${active ? ' is-selected' : ''}`}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                >
                  {opt.label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Month filter — click anywhere opens picker; typing disabled */
export function AdminMonthInput({ value, onChange, className = '', 'aria-label': ariaLabel = 'Filter by month' }) {
  const openPicker = (e) => {
    const el = e.currentTarget;
    try {
      if (typeof el.showPicker === 'function') el.showPicker();
    } catch (_) {
      /* Browser may block if not from a user gesture */
    }
  };

  return (
    <input
      type="month"
      value={value}
      aria-label={ariaLabel}
      autoComplete="off"
      className={`admin-filter-control admin-month-input${className ? ` ${className}` : ''}`}
      onChange={(e) => onChange(e.target.value)}
      onClick={openPicker}
      onFocus={openPicker}
      onKeyDown={(e) => {
        if (e.key === 'Tab' || e.key === 'Escape') return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openPicker(e);
          return;
        }
        e.preventDefault();
      }}
    />
  );
}

/** Shared page header for admin modules */
export function AdminPageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
      <div>
        <h2 className="text-xl md:text-2xl font-bold text-gray-800">{title}</h2>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function EmptyState({ icon = 'fas fa-inbox', title, text, action }) {
  return (
    <div className="py-16 text-center bg-white rounded-2xl border border-dashed border-gray-200">
      <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: '#fff7ed' }}>
        <i className={`${icon} text-2xl`} style={{ color: '#f97316' }}></i>
      </div>
      <p className="text-gray-700 font-semibold">{title}</p>
      {text && <p className="text-gray-400 text-sm mt-1 max-w-sm mx-auto">{text}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Toast({ message }) {
  if (!message) return null;
  return (
    <div className="fixed bottom-6 right-6 z-50 bg-gray-900 text-white px-5 py-3 rounded-2xl shadow-2xl text-sm font-medium flex items-center gap-2">
      {message}
    </div>
  );
}

export function PrimaryBtn({ children, className = '', type = 'button', ...props }) {
  return (
    <button type={type} className={`admin-btn-primary px-5 py-2.5 text-sm ${className}`} {...props}>
      {children}
    </button>
  );
}

/** Chrome Android shows password/card/address accessory on text/url/email/tel — not on type="search". */
const ANTI_AUTOFILL = {
  autoComplete: 'off',
  autoCapitalize: 'off',
  autoCorrect: 'off',
  spellCheck: false,
  'data-lpignore': 'true',
  'data-1p-ignore': 'true',
  'data-bwignore': 'true',
  'data-form-type': 'other',
  'aria-autocomplete': 'none',
};

/** Map types that trigger Chrome's autofill bar to type="search" (Firefox never shows that bar). */
function chromeSafeType(type, inputMode) {
  const t = type || 'text';
  if (t === 'search' || t === 'checkbox' || t === 'radio' || t === 'file' || t === 'hidden' || t === 'button' || t === 'submit' || t === 'month' || t === 'date' || t === 'password') {
    return { type: t, inputMode };
  }
  if (t === 'number') {
    return { type: 'search', inputMode: inputMode || 'numeric' };
  }
  if (t === 'email') {
    return { type: 'search', inputMode: inputMode || 'email' };
  }
  if (t === 'tel') {
    return { type: 'search', inputMode: inputMode || 'tel' };
  }
  if (t === 'url') {
    return { type: 'search', inputMode: inputMode || 'text' };
  }
  // text and anything else → search (hides Chrome Android autofill accessory)
  return { type: 'search', inputMode: inputMode || 'text' };
}

export function AdminFieldInput({
  label,
  required,
  hint,
  clearable = false,
  onClear,
  className = '',
  value,
  type = 'text',
  inputMode,
  ...props
}) {
  const showClear = clearable && value != null && String(value).length > 0;
  const safe = chromeSafeType(type, inputMode);
  return (
    <div className={className || undefined}>
      {label ? (
        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
          {label}{required ? <span className="text-red-500 ml-0.5">*</span> : null}
        </label>
      ) : null}
      <div className="admin-field-wrap">
        <input
          value={value}
          {...props}
          {...ANTI_AUTOFILL}
          type={safe.type}
          inputMode={safe.inputMode}
          className={`admin-field-input${showClear ? ' has-clear' : ''}${props.className ? ` ${props.className}` : ''}`}
        />
        {showClear ? (
          <button
            type="button"
            className="admin-field-clear"
            onClick={onClear}
            aria-label="Clear"
            title="Clear"
          >
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        ) : null}
      </div>
      {hint ? <p className="text-xs text-gray-400 mt-1">{hint}</p> : null}
    </div>
  );
}

export function AdminFieldTextarea({ label, required, hint, className = '', ...props }) {
  return (
    <div className={className || undefined}>
      {label ? (
        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
          {label}{required ? <span className="text-red-500 ml-0.5">*</span> : null}
        </label>
      ) : null}
      <textarea
        {...props}
        {...ANTI_AUTOFILL}
        className={`admin-field-textarea${props.className ? ` ${props.className}` : ''}`}
      />
      {hint ? <p className="text-xs text-gray-400 mt-1">{hint}</p> : null}
    </div>
  );
}

/**
 * URL field — type="search" (not url/text) so Chrome Android does not show
 * password / payment / address icons above the keyboard (Firefox never does).
 */
export function AdminUrlField({ value, onChange, onClear, placeholder }) {
  return (
    <div className="admin-url-field">
      <input
        type="search"
        inputMode="text"
        enterKeyHint="done"
        name="admin_link_field"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        {...ANTI_AUTOFILL}
        className="admin-url-input"
      />
      {value ? (
        <button
          type="button"
          className="admin-url-clear"
          onClick={onClear}
          aria-label="Clear URL"
          title="Clear"
        >
          <i className="fas fa-times" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

/** Labeled AdminSelect — full-width in forms */
export function AdminFieldSelect({ label, required, value, onChange, options, 'aria-label': ariaLabel, className = '' }) {
  return (
    <div className={className || undefined}>
      {label ? (
        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
          {label}{required ? <span className="text-red-500 ml-0.5">*</span> : null}
        </label>
      ) : null}
      <AdminSelect
        className="is-block"
        value={value}
        onChange={onChange}
        options={options}
        aria-label={ariaLabel || label || 'Select'}
      />
    </div>
  );
}

export function AdminModalClose({ onClick, 'aria-label': ariaLabel = 'Close' }) {
  return (
    <button type="button" className="admin-modal-close" onClick={onClick} aria-label={ariaLabel} title={ariaLabel}>
      <i className="fas fa-times" aria-hidden="true" />
    </button>
  );
}

export function AdminCheckbox({ checked, onChange, label }) {
  return (
    <label className={`admin-check${checked ? ' is-checked' : ''}`}>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="admin-check-box" aria-hidden="true">
        <i className="fas fa-check" />
      </span>
      <span className="admin-check-label">{label}</span>
    </label>
  );
}

export function ConfirmDialog({ title = 'Are you sure?', message, onConfirm, onCancel, loading, confirmLabel = 'Delete' }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
        <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: '#fee2e2' }}>
          <i className="fas fa-exclamation-triangle text-xl" style={{ color: '#ef4444' }}></i>
        </div>
        <h4 className="font-bold text-gray-800 mb-1">{title}</h4>
        <p className="text-sm text-gray-500 mb-5">{message}</p>
        <div className="flex gap-3">
          <button type="button" onClick={onCancel} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-600 font-semibold text-sm hover:bg-gray-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 py-2.5 rounded-xl font-semibold text-sm disabled:opacity-60 flex items-center justify-center gap-2"
            style={{ background: '#ef4444', color: '#fff' }}
          >
            {loading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-trash"></i>}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ActionBtn({ onClick, icon, label, color = 'gray', title }) {
  const colors = {
    gray: { bg: '#f9fafb', text: '#4b5563' },
    blue: { bg: '#eff6ff', text: '#2563eb' },
    green: { bg: '#f0fdf4', text: '#16a34a' },
    red: { bg: '#fef2f2', text: '#dc2626' },
    orange: { bg: '#fff7ed', text: '#ea580c' },
  };
  const c = colors[color] || colors.gray;
  return (
    <button
      type="button"
      onClick={onClick}
      title={title || label}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
      style={{ background: c.bg, color: c.text }}
    >
      <i className={icon}></i>
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}
